import { ArrowRightIcon, ExternalLinkIcon, ListTodoIcon, RefreshCwIcon } from "lucide-react";

import { cn } from "~/lib/utils";
import { ProjectIdentityIcon } from "../ProjectIdentityIcon";
import { Button } from "../ui/button";
import { formatPriorityDisplayLabel, formatStatusDisplayLabel } from "./taskRowFormatting";
import type { TaskContextModel, TaskContextSyncState } from "./TaskContextSurface.logic";
export { buildTaskContextModel } from "./TaskContextSurface.logic";
export type { TaskContextModel } from "./TaskContextSurface.logic";

interface TaskContextSurfaceProps {
  readonly model: TaskContextModel;
  readonly variant: "strip" | "panel";
  readonly onViewTask?: (() => void) | undefined;
  readonly onRetrySync?: (() => void) | undefined;
}

const SYNC_COPY: Record<TaskContextSyncState, string> = {
  synced: "Synced",
  pending: "Sync pending",
  failed: "Sync needs retry",
};

function StatusPill({ status }: { readonly status: string | null }) {
  return (
    <span className="inline-flex h-5 shrink-0 items-center rounded-md border border-border/60 bg-muted/20 px-1.5 text-[10px] font-medium text-muted-foreground">
      {formatStatusDisplayLabel(status)}
    </span>
  );
}

function SyncPill({ state }: { readonly state: TaskContextSyncState }) {
  return (
    <span
      className={cn(
        "inline-flex h-5 shrink-0 items-center rounded-md border px-1.5 text-[10px] font-medium",
        state === "failed"
          ? "border-warning-border bg-warning-surface text-warning-foreground"
          : state === "pending"
            ? "border-hue-sky-border bg-hue-sky-surface text-hue-sky-foreground"
            : "border-border/60 bg-muted/10 text-muted-foreground",
      )}
    >
      {SYNC_COPY[state]}
    </span>
  );
}

function TaskMeta({ model }: { readonly model: TaskContextModel }) {
  const meta = [model.campaignName, model.workspaceName].filter(
    (part): part is string => typeof part === "string" && part.trim().length > 0,
  );
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
      <span className="font-mono tabular-nums">{model.taskId}</span>
      {/* Split out of the plain string list so the project keeps its mark —
          the campaign and the workspace are not projects and stay text. */}
      {model.projectName ? (
        <span className="inline-flex min-w-0 max-w-44 items-center gap-1">
          <ProjectIdentityIcon
            className="size-3"
            icon={model.projectIcon}
            iconUrl={model.projectIconUrl}
            projectName={model.projectName}
          />
          <span className="min-w-0 truncate">{model.projectName}</span>
        </span>
      ) : null}
      {meta.map((part) => (
        <span key={part} className="min-w-0 max-w-44 truncate">
          {part}
        </span>
      ))}
      {model.priority ? (
        <span className="shrink-0">{formatPriorityDisplayLabel(model.priority)}</span>
      ) : null}
    </div>
  );
}

export function TaskContextSurface({
  model,
  variant,
  onRetrySync,
  onViewTask,
}: TaskContextSurfaceProps) {
  if (variant === "panel") {
    return (
      <section
        aria-label="CMMD task context"
        className="flex min-h-0 min-w-0 flex-1 flex-col bg-card"
        data-testid="cmmd-task-context-panel"
      >
        <div className="border-b border-border px-4 py-4">
          <div className="flex min-w-0 items-start gap-3">
            <span
              aria-hidden="true"
              className="mt-0.5 inline-flex size-8 shrink-0 items-center justify-center rounded-md border border-border/65 bg-muted/20 text-muted-foreground"
            >
              <ListTodoIcon className="size-4" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <h2 className="min-w-0 truncate text-[15px] font-semibold leading-6 text-foreground">
                  {model.title}
                </h2>
                <StatusPill status={model.status} />
                <SyncPill state={model.syncState} />
              </div>
              <TaskMeta model={model} />
            </div>
          </div>
        </div>
        <div className="grid gap-3 px-4 py-4 text-sm">
          <InfoRow
            label="Project"
            value={model.projectName ?? "Not linked"}
            {...(model.projectName
              ? { icon: { icon: model.projectIcon, iconUrl: model.projectIconUrl } }
              : {})}
          />
          <InfoRow label="Campaign" value={model.campaignName ?? "None"} />
          <InfoRow label="Workspace" value={model.workspaceName ?? "Unknown"} />
          <InfoRow label="Priority" value={formatPriorityDisplayLabel(model.priority)} />
          <InfoRow label="Status" value={formatStatusDisplayLabel(model.status)} />
        </div>
        <div className="mt-auto grid gap-2 border-t border-border px-4 py-3">
          {model.syncState === "failed" ? (
            <Button variant="outline" size="sm" onClick={onRetrySync}>
              <RefreshCwIcon aria-hidden="true" className="size-4" />
              Retry sync
            </Button>
          ) : null}
          {model.sourceUrl ? (
            <Button variant="outline" size="sm" render={<a href={model.sourceUrl} />}>
              <ExternalLinkIcon aria-hidden="true" className="size-4" />
              Open in CMMD
            </Button>
          ) : null}
        </div>
      </section>
    );
  }

  return (
    <section
      aria-label="CMMD task context"
      className="flex min-w-0 items-center gap-3 rounded-lg border border-border/65 bg-card/70 px-3 py-2"
      data-testid="cmmd-task-context-strip"
    >
      <span
        aria-hidden="true"
        className="inline-flex size-7 shrink-0 items-center justify-center rounded-md border border-border/60 bg-muted/20 text-muted-foreground"
      >
        <ListTodoIcon className="size-3.5" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <span className="min-w-0 truncate text-[13px] font-semibold text-foreground">
            {model.title}
          </span>
          <StatusPill status={model.status} />
          {model.syncState !== "synced" ? <SyncPill state={model.syncState} /> : null}
        </div>
        <TaskMeta model={model} />
      </div>
      <Button
        variant="ghost"
        size="xs"
        onClick={onViewTask}
        aria-label={`View task ${model.title}`}
      >
        <span className="hidden sm:inline">View task</span>
        <ArrowRightIcon aria-hidden="true" className="size-4" />
      </Button>
    </section>
  );
}

function InfoRow({
  label,
  value,
  icon,
}: {
  readonly label: string;
  readonly value: string;
  readonly icon?: { readonly icon: string | null; readonly iconUrl: string | null } | undefined;
}) {
  return (
    <div className="grid min-w-0 grid-cols-[92px_minmax(0,1fr)] gap-3 border-b border-border/50 pb-2 last:border-b-0">
      <span className="text-xs text-muted-foreground/70">{label}</span>
      <span className="flex min-w-0 items-center gap-1.5 text-sm text-foreground">
        {icon ? (
          <ProjectIdentityIcon
            className="size-3.5"
            icon={icon.icon}
            iconUrl={icon.iconUrl}
            projectName={value}
          />
        ) : null}
        <span className="min-w-0 truncate">{value}</span>
      </span>
    </div>
  );
}
